import React, { useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { client } from "../../Clients";
import type { BackupArchiveDto, RestoreAcceptedDto } from "./backupClient";

/**
 * Modale rouge de restauration en mode REMPLACEMENT (destructif) : la base est intégralement
 * remplacée par le contenu de l'archive : les lignes absentes de l'archive sont supprimées.
 * Garde-fou : le bouton de confirmation reste désactivé tant que l'utilisateur n'a pas tapé
 * REMPLACER. Le client envoie l'en-tête X-Confirm-Destructive exigé par l'API (pour les deux
 * modes désormais). L'API répond 202 { jobId } : la restauration tourne en tâche de fond :
 * cette modale se referme via `onAccepted` et le parent bascule sur la vue de suivi
 * (RestoreProgressModal) qui sonde le job jusqu'au bout.
 */
interface ReplaceRestoreModalProps {
	archive: BackupArchiveDto;
	onClose: () => void;
	/** Appelé dès que l'API a accepté la restauration (202) : le parent ouvre la vue de suivi. */
	onAccepted: (accepted: RestoreAcceptedDto) => void;
}

const CONFIRM_WORD = "REMPLACER";

const ReplaceRestoreModal: React.FC<ReplaceRestoreModalProps> = ({ archive, onClose, onAccepted }) => {
	const [typed, setTyped] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const confirmed = typed.normalize("NFC").trim() === CONFIRM_WORD;

	const runRestore = async () => {
		if (!confirmed || busy) return;
		setBusy(true);
		setError(null);
		try {
			const accepted = await client.backup.restore({
				backupId: archive.id,
				destinationId: archive.destinationId,
				mode: "replace",
			});
			onAccepted(accepted);
		} catch (e) {
			setError(
				(e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
					(e instanceof Error ? e.message : String(e))
			);
			setBusy(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-label="Restauration par remplacement">
			<div className="w-full max-w-lg rounded-2xl border-2 border-red-300 bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-2">
						<span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-600">
							<FiAlertTriangle className="h-5 w-5" aria-hidden />
						</span>
						<h3 className="text-base font-semibold text-red-700">Restauration par remplacement</h3>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						aria-label="Fermer"
						className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
					>
						<FiX className="h-5 w-5" aria-hidden />
					</button>
				</div>

				<p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
					⚠️ Cette opération remplace TOUTES les données de la base par le contenu de l'archive.
					Les données absentes de l'archive seront supprimées.
				</p>

				<p className="mt-3 text-sm text-slate-600">
					Archive : <code className="rounded bg-slate-100 px-1 text-xs">{archive.id}</code> : destination{" "}
					<code className="rounded bg-slate-100 px-1 text-xs">{archive.destinationId}</code>.
				</p>

				<label className="mt-4 block text-sm text-slate-700">
					Pour confirmer, tapez <span className="font-semibold">{CONFIRM_WORD}</span> :
					<input
						type="text"
						value={typed}
						onChange={(e) => setTyped(e.target.value)}
						disabled={busy}
						placeholder={CONFIRM_WORD}
						className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none"
					/>
				</label>

				{error && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">Échec : {error}</p>}

				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={runRestore}
						disabled={!confirmed || busy}
						className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{busy ? "Lancement…" : "Remplacer la base"}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ReplaceRestoreModal;
